import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate } from '@tanstack/react-router'
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Link2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Users,
  Video,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { api, type Meeting, type RsvpStatus } from '../lib/api'
import { invalidateMeetingData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { MiniPager, useEmbeddedPageSize } from './data-table'
import { ErfassenPanel } from './erfassen-panel'
import { GuestLinkAction, GuestLinkBlock, useGuestLinkCardState } from './meetings/guest-link-block'
import { Button } from './ui/button'
import { confirm } from './ui/confirm-dialog'
// Standalone /meetings route + Sidebar entry were removed — all meeting
// functionality lives in the calendar (ghost-blocks + Erfassen-Panel +
// CalendarInfoPanel "Meetings" tab). This file keeps the page body
// exported so the embedded view in CalendarInfoPanel still works.

export function MeetingsPageBody({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient()
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['meetings'],
    queryFn: () => api.listMeetings(),
    enabled: !!me?.user,
    refetchInterval: 30_000,
  })

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Meeting | null>(null)
  const [upcomingPage, setUpcomingPage] = useState(1)
  const [pastPage, setPastPage] = useState(1)
  const [upcomingPageSize, setUpcomingPageSize] = useEmbeddedPageSize('meetings-upcoming', 10)
  const [pastPageSize, setPastPageSize] = useEmbeddedPageSize('meetings-past', 10)
  // Past-meetings section is collapsible (default: collapsed in embedded mode,
  // open in standalone). Choice persists per visit-mode.
  const [pastCollapsed, setPastCollapsed] = useEmbeddedPageSize(
    'meetings-past-collapsed',
    embedded ? 1 : 0, // 1 = collapsed, 0 = open
  )

  if (meLoading) return null
  if (!me?.user) return <Navigate to="/login" />
  const myId = me.user.id
  // #751: nur Admins stellen Gastlinks aus
  const isAdmin = me.user.role === 'admin'

  const meetings = data?.meetings ?? []
  const now = new Date()
  const upcomingAll = meetings.filter((m) => new Date(m.endTime) >= now)
  const pastAll = meetings.filter((m) => new Date(m.endTime) < now).reverse()

  // Embedded mode (CalendarInfoPanel) gets pagination so a long list stays
  // usable in the narrow column. Page through each list independently.
  const upcomingTotalPages = embedded
    ? Math.max(1, Math.ceil(upcomingAll.length / upcomingPageSize))
    : 1
  const pastTotalPages = embedded ? Math.max(1, Math.ceil(pastAll.length / pastPageSize)) : 1
  const upcoming = embedded
    ? upcomingAll.slice((upcomingPage - 1) * upcomingPageSize, upcomingPage * upcomingPageSize)
    : upcomingAll
  const past = embedded
    ? pastAll.slice((pastPage - 1) * pastPageSize, pastPage * pastPageSize)
    : pastAll
  const isPastCollapsed = pastCollapsed === 1

  function refresh() {
    invalidateMeetingData(qc)
  }

  return (
    <section className={cn('space-y-4', !embedded && 'animate-fade-in')}>
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-b border-border-subtle',
          embedded ? 'pb-2' : 'pb-3',
        )}
      >
        {!embedded && (
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-lg font-medium tracking-tight text-text-primary shrink-0">
              Meetings
            </h1>
          </div>
        )}
        {/* Embedded mode hides the create button — Erfassen-Panel covers it */}
        {!embedded && (
          <div className="flex items-center gap-2 shrink-0">
            <Button onClick={() => setCreateOpen(true)} variant="primary">
              <Plus className="h-4 w-4" />
              <span>Neues Meeting</span>
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-text-tertiary">Lade …</p>
      ) : meetings.length === 0 ? (
        /* Auch embedded (Kalender-Panel) den CTA anbieten — das ErfassenPanel
           mit defaultTab="meeting" ist in beiden Modi gemountet; ohne Button
           forderte der Text zu einer Aktion ohne sichtbaren Einstieg auf. */
        <EmptyState onCreate={() => setCreateOpen(true)} />
      ) : (
        <div className="space-y-4">
          <MeetingList
            title="Anstehend"
            meetings={upcoming}
            myId={myId}
            isAdmin={isAdmin}
            onEdit={(m) => setEditing(m)}
            onChanged={refresh}
            compact={embedded}
          />
          {embedded && (
            <MiniPager
              page={upcomingPage}
              totalPages={upcomingTotalPages}
              onPage={setUpcomingPage}
              pageSize={upcomingPageSize}
              onPageSize={(n) => {
                setUpcomingPageSize(n)
                setUpcomingPage(1)
              }}
            />
          )}
          {pastAll.length > 0 && (
            <section className={cn('opacity-70')}>
              <button
                type="button"
                onClick={() => setPastCollapsed(isPastCollapsed ? 0 : 1)}
                className="mb-2 flex w-full items-center gap-1.5 text-left hover:opacity-100 transition-opacity group"
                aria-expanded={!isPastCollapsed}
              >
                {isPastCollapsed ? (
                  <ChevronRight className="h-3 w-3 text-text-tertiary group-hover:text-text-secondary" />
                ) : (
                  <ChevronDown className="h-3 w-3 text-text-tertiary group-hover:text-text-secondary" />
                )}
                <h2 className="text-xs uppercase tracking-wider text-text-tertiary group-hover:text-text-secondary">
                  Vergangen
                </h2>
                <span className="text-xs text-text-tertiary tabular-nums ml-auto">
                  {pastAll.length}
                </span>
              </button>
              {!isPastCollapsed && (
                <>
                  <ul className={cn(embedded ? 'space-y-2' : 'space-y-3')}>
                    {past.map((m) => (
                      <li key={m.id}>
                        <MeetingCard
                          meeting={m}
                          myId={myId}
                          isAdmin={isAdmin}
                          onEdit={() => setEditing(m)}
                          onChanged={refresh}
                          compact={embedded}
                        />
                      </li>
                    ))}
                  </ul>
                  {embedded && (
                    <MiniPager
                      page={pastPage}
                      totalPages={pastTotalPages}
                      onPage={setPastPage}
                      pageSize={pastPageSize}
                      onPageSize={(n) => {
                        setPastPageSize(n)
                        setPastPage(1)
                      }}
                    />
                  )}
                </>
              )}
            </section>
          )}
        </div>
      )}

      {/* Unified Erfassen-Panel — Meeting-Tab is the default here. In
          edit-mode (editing != null) the tab is locked to Meeting; otherwise
          the user can swap to Eintrag/Abwesenheit while staying in the same
          panel. */}
      <ErfassenPanel
        open={createOpen || !!editing}
        onClose={() => {
          setCreateOpen(false)
          setEditing(null)
        }}
        defaultTab="meeting"
        meeting={editing}
        myId={myId}
        onSaved={refresh}
      />
    </section>
  )
}

function EmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-12 text-center">
      <Video className="h-12 w-12 mx-auto text-text-tertiary/60 mb-3" strokeWidth={1.5} />
      <h2 className="font-serif text-2xl">Noch keine Meetings</h2>
      <p className="mt-2 text-sm text-text-tertiary max-w-md mx-auto">
        Leg ein Meeting an, lade Kolleg:innen ein und es taucht im Kalender und als
        Besprechungs-Option in deinen Zeiteinträgen auf.
      </p>
      {onCreate && (
        <Button onClick={onCreate} className="mt-5">
          <Plus className="h-4 w-4" /> Erstes Meeting anlegen
        </Button>
      )}
    </div>
  )
}

// ============================================================================
// Meeting list
// ============================================================================

function MeetingList({
  title,
  meetings,
  myId,
  isAdmin,
  onEdit,
  onChanged,
  dimmed = false,
  compact = false,
}: {
  title: string
  meetings: Meeting[]
  myId: string
  isAdmin: boolean
  onEdit: (m: Meeting) => void
  onChanged: () => void
  dimmed?: boolean
  compact?: boolean
}) {
  if (meetings.length === 0) return null
  return (
    <section className={cn(dimmed && 'opacity-70')}>
      <header className="mb-2 flex items-baseline justify-between">
        <h2 className="text-xs uppercase tracking-wider text-text-tertiary">{title}</h2>
        <span className="text-xs text-text-tertiary tabular-nums">{meetings.length}</span>
      </header>
      <ul className={cn(compact ? 'space-y-2' : 'space-y-3')}>
        {meetings.map((m) => (
          <li key={m.id}>
            <MeetingCard
              meeting={m}
              myId={myId}
              isAdmin={isAdmin}
              onEdit={() => onEdit(m)}
              onChanged={onChanged}
              compact={compact}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

function MeetingCard({
  meeting,
  myId,
  isAdmin,
  onEdit,
  onChanged,
  compact = false,
}: {
  meeting: Meeting
  myId: string
  isAdmin: boolean
  onEdit: () => void
  onChanged: () => void
  compact?: boolean
}) {
  const isMine = meeting.createdBy === myId
  const my = meeting.participants.find((p) => p.userId === myId)
  const myStatus = my?.status ?? null

  const setRsvp = useMutation({
    mutationFn: (status: RsvpStatus) => api.setMeetingRsvp(meeting.id, status),
    onSuccess: () => onChanged(),
    onError: (err: Error) => notify.error(err.message),
  })

  const del = useMutation({
    mutationFn: () => api.deleteMeeting(meeting.id),
    onSuccess: () => {
      notify.success('Meeting gelöscht')
      onChanged()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const start = new Date(meeting.startTime)
  const end = new Date(meeting.endTime)

  const guest = useGuestLinkCardState()
  const guestAction = (
    <GuestLinkAction
      meeting={meeting}
      isAdmin={isAdmin}
      expanded={guest.expanded}
      onToggle={guest.toggle}
      onIssued={guest.onIssued}
    />
  )
  const guestBlock = guest.expanded ? (
    <GuestLinkBlock
      meeting={meeting}
      isAdmin={isAdmin}
      issued={guest.issued}
      onIssued={guest.onIssued}
      onRevoked={guest.onRevoked}
    />
  ) : null

  if (compact) {
    // Compact card for the CalendarInfoPanel side-bar (~380-420px wide).
    // Drops the avatar, the per-participant chips and the description; the
    // user can still tap "Bearbeiten" for the full picture inside ErfassenPanel.
    const timeFmt = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })
    const dateFmt = new Intl.DateTimeFormat('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    })
    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    const timeLabel = sameDay
      ? `${dateFmt.format(start)} · ${timeFmt.format(start)}–${timeFmt.format(end)}`
      : `${dateFmt.format(start)} ${timeFmt.format(start)} → ${dateFmt.format(end)}`
    return (
      <article className="rounded-xl border border-border-subtle bg-bg-elevated/40 p-3 space-y-2">
        <div className="flex items-start gap-2">
          <h3 className="font-medium text-sm text-text-primary truncate flex-1 min-w-0">
            {meeting.title}
          </h3>
          <span
            className="inline-flex items-center gap-1 rounded-full bg-bg-overlay/60 text-text-tertiary px-1.5 py-0.5 text-2xs tabular-nums shrink-0"
            title={`${meeting.participants.length} Teilnehmer`}
          >
            <Users className="h-3 w-3" />
            {meeting.participants.length}
          </span>
        </div>
        <p className="text-[11px] text-text-tertiary flex items-center gap-1">
          <Calendar className="h-3 w-3 shrink-0" />
          <span className="truncate">{timeLabel}</span>
        </p>
        <div className="flex items-center gap-1 pt-1 border-t border-border-subtle/60">
          {meeting.onlineUrl && <JoinButton meeting={meeting} />}
          {guestAction}
          <div className="flex-1" />
          {myStatus && myStatus !== 'accepted' && (
            <>
              <button
                type="button"
                onClick={() => setRsvp.mutate('accepted')}
                disabled={setRsvp.isPending}
                className="p-1.5 rounded text-success hover:bg-success/15"
                title="Annehmen"
                aria-label="Annehmen"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              {myStatus !== 'declined' && (
                <button
                  type="button"
                  onClick={() => setRsvp.mutate('declined')}
                  disabled={setRsvp.isPending}
                  className="p-1.5 rounded text-danger hover:bg-danger/15"
                  title="Ablehnen"
                  aria-label="Ablehnen"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
          {isMine && (
            <>
              <button
                type="button"
                onClick={onEdit}
                className="p-1.5 rounded text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/50"
                title="Bearbeiten"
                aria-label="Bearbeiten"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (await confirm({ title: 'Meeting löschen?' })) del.mutate()
                }}
                className="p-1.5 rounded text-text-tertiary hover:text-state-danger hover:bg-state-danger/10"
                title="Löschen"
                aria-label="Löschen"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
        {guestBlock}
      </article>
    )
  }

  return (
    <article className="rounded-xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
      <div className="px-5 py-4 flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
          <Video className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-medium text-text-primary truncate">{meeting.title}</h3>
            {isMine && (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={onEdit}
                  className="p-1.5 rounded text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/50"
                  title="Bearbeiten"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (await confirm({ title: 'Meeting löschen?' })) del.mutate()
                  }}
                  className="p-1.5 rounded text-text-tertiary hover:text-state-danger hover:bg-state-danger/10"
                  title="Löschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
          <p className="text-xs text-text-tertiary flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {formatRange(start, end)}
          </p>
          {meeting.description && (
            <p className="text-sm text-text-secondary whitespace-pre-wrap">{meeting.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1">
            {meeting.onlineUrl && <JoinButton meeting={meeting} />}
            {guestAction}
            {meeting.location && (
              <span className="inline-flex items-center gap-1.5 text-xs text-text-tertiary">
                <MapPin className="h-3.5 w-3.5" />
                {meeting.location}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 text-xs text-text-tertiary">
              <Users className="h-3.5 w-3.5" />
              {meeting.participants.length} Teilnehmer
            </span>
          </div>
          {guestBlock && <div className="pt-2">{guestBlock}</div>}
        </div>
      </div>

      {meeting.participants.length > 0 && (
        <div className="border-t border-border-subtle bg-bg-overlay/20 px-5 py-2.5 flex flex-wrap items-center gap-2">
          {meeting.participants.map((p) => (
            <span
              key={p.userId}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]',
                p.status === 'accepted' && 'bg-state-success/15 text-state-success',
                p.status === 'declined' && 'bg-state-danger/10 text-state-danger',
                p.status === 'invited' && 'bg-bg-overlay/60 text-text-tertiary',
              )}
              title={statusLabel(p.status)}
            >
              {p.status === 'accepted' && <Check className="h-3 w-3" />}
              {p.status === 'declined' && <X className="h-3 w-3" />}
              {p.name}
            </span>
          ))}
        </div>
      )}

      {myStatus && myStatus !== 'accepted' && (
        <div className="border-t border-border-subtle bg-bg-overlay/30 px-5 py-2.5 flex items-center gap-2">
          <span className="text-xs text-text-tertiary mr-auto">Deine Antwort:</span>
          <Button
            size="sm"
            variant={myStatus === 'invited' ? 'primary' : 'ghost'}
            onClick={() => setRsvp.mutate('accepted')}
            disabled={setRsvp.isPending}
          >
            <Check className="h-3.5 w-3.5" /> Annehmen
          </Button>
          {myStatus !== 'declined' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRsvp.mutate('declined')}
              disabled={setRsvp.isPending}
            >
              <X className="h-3.5 w-3.5" /> Ablehnen
            </Button>
          )}
        </div>
      )}
    </article>
  )
}

function statusLabel(s: RsvpStatus): string {
  if (s === 'accepted') return 'Zugesagt'
  if (s === 'declined') return 'Abgesagt'
  return 'Eingeladen'
}

// "Beitreten" button — for Jitsi-managed meetings, fetches a fresh JWT
// URL and opens it in a new tab. For BYO URLs (Zoom/Teams/...), just
// opens the stored url directly. Done on click rather than on render
// because the JWT is per-user + time-limited; rendering a long-lived
// URL into the DOM would defeat the auth purpose.
function JoinButton({ meeting }: { meeting: Meeting }) {
  const [loading, setLoading] = useState(false)
  const isManaged = !!meeting.roomSlug
  async function onClick() {
    if (loading) return
    if (!isManaged && meeting.onlineUrl) {
      window.open(meeting.onlineUrl, '_blank', 'noreferrer')
      return
    }
    // Open the tab SYNCHRONOUSLY inside the click handler so Safari/iOS
    // counts it as a user-gesture-driven popup. Then await the join URL
    // and redirect the already-open window. If Safari blocks the popup
    // anyway (returns null), fall back to navigating the current tab.
    const popup = window.open('about:blank', '_blank')
    try {
      setLoading(true)
      const { joinUrl } = await api.getMeetingJoinUrl(meeting.id)
      if (!joinUrl) {
        popup?.close()
        notify.error('Kein Beitritts-Link verfügbar')
        return
      }
      if (popup && !popup.closed) {
        popup.location.href = joinUrl
      } else {
        // Popup blocked — same-tab navigation always works.
        window.location.href = joinUrl
      }
    } catch (err) {
      popup?.close()
      notify.error((err as Error).message)
    } finally {
      setLoading(false)
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 rounded-md bg-accent/10 px-2 py-1 text-xs font-medium text-accent hover:bg-accent/20 transition-colors disabled:opacity-50"
      title={meeting.onlineUrl ?? undefined}
    >
      <Link2 className="h-3.5 w-3.5" />
      {loading ? '…' : isManaged ? 'Beitreten' : 'Externer Link'}
    </button>
  )
}

function formatRange(start: Date, end: Date): string {
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()
  const dateFmt = new Intl.DateTimeFormat('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const timeFmt = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })
  if (sameDay) {
    return `${dateFmt.format(start)} · ${timeFmt.format(start)}–${timeFmt.format(end)}`
  }
  return `${dateFmt.format(start)} ${timeFmt.format(start)} → ${dateFmt.format(end)} ${timeFmt.format(end)}`
}
